import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  RunSystemArchivingDto,
  RunSystemDto,
  RunSystemEditDto
} from 'src/store/runsystems/runsystem';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum RunSystemTypes {
  FetchAll = 'runSystems/FetchAll',
  Add = 'runSystems/Add',
  Update = 'runSystems/Update',
  Delete = 'runSystems/Delete',
  Archiving = 'runSystems/Archiving',
  Failure = 'runSystems/Failure'
}

export type RunSystemAction =
  | { type: RunSystemTypes.FetchAll; runSystems: RunSystemDto[] }
  | { type: RunSystemTypes.Add; runSystem: RunSystemDto }
  | { type: RunSystemTypes.Update; runSystem: RunSystemDto }
  | { type: RunSystemTypes.Delete; runSystem: RunSystemDto }
  | {
      type: RunSystemTypes.Archiving;
      runSystemArchiving: RunSystemArchivingDto[];
    }
  | { type: RunSystemTypes.Failure; errors: any };

export const actionCreators = {
  fetchRunSystems: (runSystems: RunSystemDto[]): RunSystemAction => ({
    type: RunSystemTypes.FetchAll,
    runSystems
  }),
  addRunSystem: (runSystem: RunSystemDto): RunSystemAction => ({
    type: RunSystemTypes.Add,
    runSystem
  }),
  updateRunSystem: (runSystem: RunSystemDto): RunSystemAction => ({
    type: RunSystemTypes.Update,
    runSystem
  }),
  deleteRunSystem: (runSystem: RunSystemDto): RunSystemAction => ({
    type: RunSystemTypes.Delete,
    runSystem
  }),
  fetchRunSystemArchiving: (
    runSystemArchiving: RunSystemArchivingDto[]
  ): RunSystemAction => ({
    type: RunSystemTypes.Archiving,
    runSystemArchiving
  }),
  runSystemRequestFailure: (errors: any): RunSystemAction => ({
    type: RunSystemTypes.Failure,
    errors
  })
};

export const fetchRunSystems = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(RunSystemTypes.FetchAll));
    const { ok, data, error } = await getRequest<RunSystemDto[]>('/runSystems');
    dispatch(requestEnd(RunSystemTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchRunSystems(data))
      : dispatch(actionCreators.runSystemRequestFailure(error));
  };
};

export const createRunSystem = (runSystem: RunSystemEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/runSystems', runSystem);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('runSystem.created')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.runSystemRequestFailure(error));
  };
};

export const editRunSystem = (id: number, runSystem: RunSystemEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/runSystems/${id}`, runSystem);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('runSystem.updated')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.runSystemRequestFailure(error));
  };
};

export const deleteRunSystem = (id: number, runSystem: RunSystemDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/runSystems/${id}`, runSystem);
    if (ok) {
      dispatch(alertSuccess(i18n.t('runSystem.deleted')));
      return dispatch(actionCreators.deleteRunSystem(runSystem));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.runSystemRequestFailure(error));
  };
};

export const fetchRunSystemArchiving = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(RunSystemTypes.Archiving));
    const { ok, data, error } = await getRequest<RunSystemArchivingDto[]>(
      '/runSystems/archiving'
    );
    dispatch(requestEnd(RunSystemTypes.Archiving));
    return ok
      ? dispatch(actionCreators.fetchRunSystemArchiving(data))
      : dispatch(actionCreators.runSystemRequestFailure(error));
  };
};
